/**
 * ai-logs-tab.tsx — Theo dõi › "Nhật ký trả lời": các lượt nhân viên AI trả lời gần đây + chi tiết từng bước xử lý.
 * (Tách từ tab "Nhật ký AI" của trang AI cũ.)
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ScrollText } from 'lucide-react'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { formatNumber } from '@/lib/utils'
import { AI_MODE_LABEL } from '@/lib/ai-khung'
import { useReplyRuns, fetchTrace, type AiMode, type ReplyRun, type AiTrace } from '@/hooks/use-ai'

const modeLabel = (m: string | null) => AI_MODE_LABEL[(m ?? '') as AiMode] ?? m ?? '—'

const STATUS_LABEL: Record<string, string> = {
  get sent() { return i18n.t('aiOps:logs.status.sent') },
  get success() { return i18n.t('aiOps:logs.status.success') },
  get completed() { return i18n.t('aiOps:logs.status.completed') },
  get failed() { return i18n.t('aiOps:logs.status.failed') },
  get error() { return i18n.t('aiOps:logs.status.error') },
  get skipped() { return i18n.t('aiOps:logs.status.skipped') },
  get suppressed() { return i18n.t('aiOps:logs.status.suppressed') },
  get suggested() { return i18n.t('aiOps:logs.status.suggested') },
  get handoff() { return i18n.t('aiOps:logs.status.handoff') },
  get pending() { return i18n.t('aiOps:logs.status.pending') },
}

function statusBadge(status: string | null) {
  const s = status ?? ''
  const label = STATUS_LABEL[s] ?? (s || '—')
  if (['sent', 'success', 'completed'].includes(s)) return <Badge variant="success">{label}</Badge>
  if (['failed', 'error'].includes(s)) return <Badge variant="destructive">{label}</Badge>
  if (['skipped', 'suppressed'].includes(s)) return <Badge variant="secondary">{label}</Badge>
  return <Badge variant="outline">{label}</Badge>
}

export function AiLogsTab() {
  const { t } = useTranslation('aiOps')
  const { data, isLoading, isError } = useReplyRuns(50)
  const [selected, setSelected] = useState<ReplyRun | null>(null)

  const cols: Column<ReplyRun>[] = [
    {
      key: 'createdAt', header: t('logs.cols.time'),
      cell: (r) => <span className="text-muted-foreground">{new Date(r.createdAt).toLocaleString(currentLocale())}</span>,
    },
    { key: 'mode', header: t('logs.cols.mode'), cell: (r) => modeLabel(r.mode) },
    { key: 'status', header: t('logs.cols.status'), cell: (r) => statusBadge(r.status) },
    {
      key: 'handoff', header: t('logs.cols.handoff'), align: 'center',
      cell: (r) => (r.handoff ? <Badge variant="warning">{t('common:misc.yes')}</Badge> : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'confidence', header: t('logs.cols.confidence'), align: 'right',
      cell: (r) => (r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—'),
    },
    {
      key: 'latencyMs', header: t('logs.cols.latency'), align: 'right',
      cell: (r) => (r.latencyMs != null ? `${formatNumber(r.latencyMs)} ms` : '—'),
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-primary" /> {t('logs.recentTitle')}
        </CardTitle>
        <CardDescription>{t('logs.recentDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loading label={t('logs.loading')} />
        ) : isError ? (
          <ErrorState message={t('logs.loadError')} />
        ) : (
          <DataTable
            columns={cols} rows={data?.runs ?? []} rowKey={(r) => r.id}
            onRowClick={(r) => setSelected(r)}
            emptyTitle={t('logs.empty')}
          />
        )}
      </CardContent>
      <TraceDialog run={selected} onClose={() => setSelected(null)} />
    </Card>
  )
}

function TraceDialog({ run, onClose }: { run: ReplyRun | null; onClose: () => void }) {
  const { t } = useTranslation('aiOps')
  const [traces, setTraces] = useState<AiTrace[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!run) return
    let alive = true
    setLoading(true)
    setTraces([])
    fetchTrace(run.id)
      .then((res) => { if (alive) setTraces(res.traces) })
      .catch((err) => toast.error(apiError(err)))
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [run])

  return (
    <Dialog open={!!run} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('logs.detailTitle')}</DialogTitle>
          <DialogDescription>
            {run ? `${modeLabel(run.mode)} · ${new Date(run.createdAt).toLocaleString(currentLocale())}` : ''}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <Loading label={t('logs.loadingSteps')} />
        ) : traces.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('logs.noSteps')}</p>
        ) : (
          <div className="space-y-3">
            {traces.map((tr) => (
              <div key={tr.id} className="rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{tr.step}</span>
                  <div className="flex items-center gap-2">
                    {tr.latencyMs != null && (
                      <span className="text-xs text-muted-foreground">{formatNumber(tr.latencyMs)} ms</span>
                    )}
                    <Badge variant={tr.level === 'error' ? 'destructive' : 'outline'}>{tr.level === 'error' ? t('common:status.error') : tr.level}</Badge>
                  </div>
                </div>
                {tr.payload != null && (
                  <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-muted p-2 text-xs">
                    {JSON.stringify(tr.payload, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
